// src/components/common/ListPager.tsx
//
// "Showing X–Y of Z" + Previous / Next for an offset-paginated list. The
// numbers come from the server's `pagination` ({ total, limit, offset }), so
// what the reader is told always matches what the server counted — the lists
// that use this used to show only the first server page with no sign that
// anything was missing.

interface ListPagerProps {
  total: number;
  offset: number;
  /** Rows on the current page. */
  count: number;
  pageSize: number;
  onOffsetChange: (offset: number) => void;
  /** Singular / plural noun for the count, e.g. ['contact', 'contacts']. */
  noun: [string, string];
  testId?: string;
}

export default function ListPager({
  total,
  offset,
  count,
  pageSize,
  onOffsetChange,
  noun,
  testId = 'list-pager',
}: ListPagerProps) {
  if (total <= 0) return null;
  const first = count > 0 ? offset + 1 : 0;
  const last = offset + count;
  const hasPrev = offset > 0;
  const hasNext = last < total;
  const fmt = (n: number) => n.toLocaleString();

  return (
    <nav
      aria-label="Pagination"
      data-testid={testId}
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-600"
    >
      <p data-testid={`${testId}-summary`}>
        {total <= count && offset === 0
          ? `Showing all ${fmt(total)} ${total === 1 ? noun[0] : noun[1]}`
          : `Showing ${fmt(first)}–${fmt(last)} of ${fmt(total)} ${total === 1 ? noun[0] : noun[1]}`}
      </p>
      {(hasPrev || hasNext) && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
            disabled={!hasPrev}
            data-testid={`${testId}-prev`}
            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => onOffsetChange(offset + pageSize)}
            disabled={!hasNext}
            data-testid={`${testId}-next`}
            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </nav>
  );
}
